import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Menu, 
  ChevronDown, 
  Search, 
  Archive, 
  EyeOff, 
  Tag, 
  Plus, 
  ZoomOut,
  MoreVertical,
  StickyNote,
  Calendar
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/ThemeToggle';
import { TagManagementDialog } from '@/components/kanban/TagManagementDialog';
import { useBoardContext } from '@/contexts/BoardContext';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';

interface MobileHeaderProps {
  currentColumn?: { title: string };
  filteredCards: any[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showSearch: boolean;
  onSearchToggle: () => void;
  isOverviewMode: boolean;
  onOverviewToggle: () => void;
  showArchivedCards: boolean;
  onArchivedToggle: () => void;
  onCardCreate: () => void;
  gestureState?: { gestureType: string; scale: number };
}

export const MobileHeader = ({
  currentColumn,
  filteredCards,
  searchQuery,
  onSearchChange,
  showSearch,
  onSearchToggle,
  isOverviewMode,
  onOverviewToggle,
  showArchivedCards,
  onArchivedToggle,
  onCardCreate,
  gestureState
}: MobileHeaderProps) => {
  const { boards, currentBoardId, setCurrentBoardId, columns } = useBoardContext();
  const navigate = useNavigate();
  const [showBoardSwitcher, setShowBoardSwitcher] = useState(false);
  const { isSmallPhone, isTablet, width } = useScreenSize();

  const currentBoard = boards?.find(board => board.id === currentBoardId);
  const archivedCardCount = filteredCards?.filter(card => card.archived).length || 0;

  const handleBoardSwitch = (boardId: string) => {
    setCurrentBoardId(boardId);
    setShowBoardSwitcher(false);
  };

  // Responsive padding and sizing
  const headerPadding = isSmallPhone ? 'px-3 py-2' : isTablet ? 'px-6 py-4' : 'px-4 py-3';
  const buttonSize = isSmallPhone ? 'sm' : 'sm';
  const titleSize = isSmallPhone ? 'text-base' : isTablet ? 'text-xl' : 'text-lg';
  const maxTitleWidth = isSmallPhone ? 'max-w-[140px]' : isTablet ? 'max-w-[300px]' : 'max-w-[200px]';

  if (showSearch) {
    return (
      <div className={`flex-shrink-0 bg-background border-b border-border ${headerPadding}`}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search cards..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`pl-10 h-9 text-sm ${isTablet ? 'h-11 text-base' : ''}`}
              autoFocus
            />
          </div>
          <Button
            variant="ghost"
            size={buttonSize}
            onClick={() => {
              onSearchToggle();
              onSearchChange('');
            }}
            className={`text-xs ${isTablet ? 'text-sm px-4' : 'px-3'} min-h-touch-target-sm`}
          >
            Cancel
          </Button>
        </div>
        
        {searchQuery.trim() && (
          <div className={`${isSmallPhone ? 'mt-1' : 'mt-2'}`}>
            <span className={`text-muted-foreground ${isSmallPhone ? 'text-xs' : 'text-sm'}`}>
              {filteredCards.length} card{filteredCards.length !== 1 ? 's' : ''} found
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 bg-background border-b border-border">
      <div className={`flex items-center justify-between ${headerPadding}`}>
        {/* Left: Board Title with Switcher */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <DropdownMenu open={showBoardSwitcher} onOpenChange={setShowBoardSwitcher}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className={`h-auto p-0 flex items-center gap-2 ${maxTitleWidth} min-h-touch-target-sm`}
              >
                <h2 className={`font-semibold truncate ${titleSize}`}>
                  {currentBoard?.title || 'Kanban Board'}
                </h2>
                {boards && boards.length > 1 && (
                  <ChevronDown className={`text-muted-foreground flex-shrink-0 ${isSmallPhone ? 'h-3 w-3' : 'h-4 w-4'}`} />
                )}
              </Button>
            </DropdownMenuTrigger>
            {boards && boards.length > 1 && (
              <DropdownMenuContent 
                align="start" 
                className={`${isTablet ? 'w-64' : 'w-56'} bg-background z-50`}
              >
                {boards.map((board) => (
                  <DropdownMenuItem
                    key={board.id}
                    onClick={() => handleBoardSwitch(board.id)}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{board.title}</span>
                    {board.id === currentBoardId && (
                      <Badge variant="secondary" className="text-xs">Current</Badge>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <Menu className="h-4 w-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/notes')}>
                  <StickyNote className="h-4 w-4 mr-2" />
                  Notes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/calendar')}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Calendar
                </DropdownMenuItem>
              </DropdownMenuContent>
            )}
          </DropdownMenu>
        </div>

        {/* Right: Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size={buttonSize} 
              className={`${isSmallPhone ? 'h-8 w-8' : 'h-8 w-8'} p-0 min-h-touch-target-sm min-w-touch-target-sm`}
            >
              <MoreVertical className={`${isSmallPhone ? 'h-3 w-3' : 'h-4 w-4'}`} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className={`${isTablet ? 'w-52' : 'w-48'} bg-background z-50`}
          >
            <DropdownMenuItem onClick={onCardCreate}>
              <Plus className="h-4 w-4 mr-2" />
              New Card
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onSearchToggle}>
              <Search className="h-4 w-4 mr-2" />
              Search Cards
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOverviewToggle}>
              <ZoomOut className="h-4 w-4 mr-2" />
              {isOverviewMode ? 'Exit Overview' : 'Overview Mode'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onArchivedToggle}>
              {showArchivedCards ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide Archived
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Show Archived
                  {archivedCardCount > 0 && (
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {archivedCardCount}
                    </Badge>
                  )}
                </>
              )}
            </DropdownMenuItem>
            <TagManagementDialog>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Tag className="h-4 w-4 mr-2" />
                Manage Tags
              </DropdownMenuItem>
            </TagManagementDialog>
            <DropdownMenuSeparator />
            <div className="px-2 py-1">
              <ThemeToggle />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Column Info Bar */}
      {!isOverviewMode && currentColumn && (
        <div className={`bg-muted/30 border-t border-border ${headerPadding.replace('py-', 'py-')}`}>
          <div className="flex items-center justify-between">
            <span className={`font-medium ${isSmallPhone ? 'text-xs' : 'text-sm'}`}>{currentColumn.title}</span>
            <span className={`text-muted-foreground ${isSmallPhone ? 'text-xs' : 'text-xs'}`}>
              {filteredCards.length} card{filteredCards.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Gesture Feedback */}
      {gestureState?.gestureType === 'pinch' && (
        <div className={`bg-blue-50 dark:bg-blue-950 border-t border-blue-200 dark:border-blue-800 ${headerPadding.replace('py-', 'py-1')}`}>
          <div className={`text-blue-700 dark:text-blue-300 ${isSmallPhone ? 'text-xs' : 'text-xs'}`}>
            Pinch to zoom • Scale: {gestureState.scale.toFixed(1)}x
          </div>
        </div>
      )}
    </div>
  );
};
